import { useState, type FormEvent, type InputHTMLAttributes } from "react";
import { readKioskDeviceToken } from "../api";
import teddifyLogo from "../assets/brand/teddify-logo.svg";
import { Button } from "./ui/Button";
import { Field, Input } from "./ui/Field";
import { Feedback } from "./ui/Surface";
import "./LoginScreen.css";

interface LoginScreenProps {
  error: string | null;
  busy: boolean;
  onLogin: (username: string, password: string) => Promise<void>;
  onSignup: (input: { username: string; nickname: string; password: string }) => Promise<boolean>;
  onWorkerMode: (deviceToken?: string) => Promise<boolean>;
  onClearError: () => void;
  deviceOnly?: boolean;
}

function SecretInput({ secretLabel = "비밀번호", ...props }: InputHTMLAttributes<HTMLInputElement> & { secretLabel?: string }) {
  const [visible, setVisible] = useState(false);
  return <div className="login-secret">
    <Input {...props} type={visible ? "text" : "password"} />
    <button type="button" aria-label={`${secretLabel} ${visible ? "숨기기" : "표시"}`} aria-pressed={visible}
      disabled={props.disabled} onClick={() => setVisible(value => !value)}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
        {visible && <path d="m4 3 16 18" />}
      </svg>
    </button>
  </div>;
}

export function LoginScreen({ error, busy, onLogin, onSignup, onWorkerMode, onClearError, deviceOnly = false }: LoginScreenProps) {
  const [mode, setMode] = useState<"login" | "signup" | "kiosk">(deviceOnly ? "kiosk" : "login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [signupNotice, setSignupNotice] = useState<string | null>(null);
  const [deviceToken, setDeviceToken] = useState("");
  const submitLogin = (event: FormEvent) => { event.preventDefault(); if (!busy) void onLogin(username, password); };
  const submitSignup = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setSignupNotice(null);
    if (password.length < 8) { setSignupNotice("비밀번호를 8자 이상 입력해 주세요."); return; }
    if (password !== passwordConfirm) { setSignupNotice("비밀번호 확인이 일치하지 않습니다."); return; }
    if (await onSignup({ username, nickname, password })) {
      setSignupNotice("가입 신청을 보냈습니다. 관리자가 승인하면 로그인할 수 있습니다.");
      setPassword(""); setPasswordConfirm("");
    }
  };
  const changeMode = (next: "login" | "signup") => {
    if (busy) return;
    setMode(next); setSignupNotice(null); setPassword(""); setPasswordConfirm(""); setDeviceToken(""); onClearError();
  };
  const openWorkerMode = async () => {
    if (busy) return;
    const saved = readKioskDeviceToken();
    if (saved && await onWorkerMode()) return;
    if (import.meta.env.DEV && !saved && await onWorkerMode()) return;
    setMode("kiosk");
  };
  const submitKiosk = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (await onWorkerMode(deviceToken.trim())) setDeviceToken("");
  };
  const title = mode === "login" ? "로그인" : mode === "signup" ? "회원가입" : "현장 장치 인증";
  return <main className="login-page" data-testid="login-screen" data-mode={mode}>
    <div className="login-brand">
      <div className="login-brand-content">
        {/* Crop the existing logo's artboard, not the mark; no replacement brand asset. */}
        <svg className="login-brand-logo" viewBox="96 216 654 196" role="img" aria-label="TEDDIFY">
          <image href={teddifyLogo} width="842" height="595" />
        </svg>
        <p>작업실 관리시스템</p>
      </div>
    </div>
    <section className="login-card" aria-label={mode === "login" ? "로그인" : undefined} aria-labelledby={mode === "login" ? undefined : "login-title"}>
      <div className="login-card-content">
      {mode !== "login" && <header className="login-heading"><h1 id="login-title">{title}</h1></header>}
      <div className="login-feedback" aria-live="polite">{(error || signupNotice) && <Feedback tone={error ? "error" : "info"}>{error ?? signupNotice}</Feedback>}</div>
      {mode === "login" ? <form className="login-form" onSubmit={submitLogin} aria-busy={busy}>
        <Field label="아이디" required><Input id="loginUsername" data-testid="login-username" value={username} onChange={event => setUsername(event.target.value)} required autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus disabled={busy} /></Field>
        <Field label="비밀번호" required><SecretInput id="loginPassword" data-testid="login-password" value={password} onChange={event => setPassword(event.target.value)} required autoComplete="current-password" disabled={busy} /></Field>
        <Button className="login-submit" type="submit" variant="primary" busy={busy}>{busy ? "로그인 중…" : "로그인"}</Button>
        <Button className="login-signup" variant="quiet" disabled={busy} onClick={() => changeMode("signup")}>회원가입</Button>
      </form> : mode === "signup" ? <form className="login-form" onSubmit={event => void submitSignup(event)} aria-busy={busy}>
        <Field label="아이디" required><Input value={username} onChange={event => setUsername(event.target.value)} minLength={3} maxLength={100} placeholder="3자 이상" required autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus disabled={busy} /></Field>
        <Field label="이름" required><Input value={nickname} onChange={event => setNickname(event.target.value)} maxLength={100} required autoComplete="name" disabled={busy} /></Field>
        <Field label="비밀번호" required><SecretInput value={password} onChange={event => setPassword(event.target.value)} minLength={8} maxLength={200} placeholder="8자 이상" required autoComplete="new-password" disabled={busy} /></Field>
        <Field label="비밀번호 확인" required><SecretInput secretLabel="비밀번호 확인" value={passwordConfirm} onChange={event => setPasswordConfirm(event.target.value)} minLength={8} maxLength={200} required autoComplete="new-password" disabled={busy} /></Field>
        <Button className="login-submit" variant="primary" type="submit" busy={busy}>{busy ? "신청 중…" : "가입 신청"}</Button>
        <Button variant="quiet" disabled={busy} onClick={() => changeMode("login")}>로그인으로 돌아가기</Button>
      </form> : <form className="login-form" onSubmit={event => void submitKiosk(event)} aria-busy={busy}>
        <Field label="장치 인증키" required><SecretInput secretLabel="인증키" value={deviceToken} onChange={event => setDeviceToken(event.target.value)} required minLength={8} maxLength={8} pattern="[A-Za-z0-9]{8}" autoComplete="off" autoCapitalize="none" spellCheck={false} autoFocus disabled={busy} /></Field>
        <Button className="login-submit" variant="primary" type="submit" busy={busy}>{busy ? "확인 중…" : "현장 화면 열기"}</Button>
        {!deviceOnly && <Button variant="quiet" disabled={busy} onClick={() => changeMode("login")}>로그인으로 돌아가기</Button>}
      </form>}
      {mode === "login" && <footer className="login-device-entry">
        <Button variant="quiet" disabled={busy} onClick={() => void openWorkerMode()}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M10 18h4" /></svg>
          완성작업 입력
        </Button>
      </footer>}
      </div>
    </section>
  </main>;
}
